// Hand-rolled SVG port of StationDetailView.tideChart: Catmull-Rom hourly
// curve with a soft area wash, high/low markers labelled with their heights,
// day gridlines, plus a crosshair/tooltip layer (pointer and keyboard).
import { useEffect, useMemo, useRef, useState } from 'react';
import type { JSX, KeyboardEvent, PointerEvent } from 'react';
import type { TideExtreme, TidePredictionPoint } from '../../types/stations';
import { formatDateTime } from '../../lib/format';
import {
  catmullRomPath,
  dayTicks,
  describeTideChart,
  extremeNear,
  formatFeet,
  formatMonthDay,
  linearScale,
  nearestIndex,
  timeDomain,
  valueDomain,
} from './tideModel';

// Plot height 194 plus a 30px band for the day labels, so a low tide at the
// domain minimum still has room for its value above the axis text.
const HEIGHT = 224;
const MARGIN = { top: 20, right: 14, bottom: 30, left: 40 };
const FALLBACK_WIDTH = 340;
/** A hover position within half an hour of a high/low event names that event. */
const EXTREME_TOLERANCE_MS = 31 * 60 * 1000;

function useContainerWidth() {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.getBoundingClientRect().width);
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) setWidth(entry.contentRect.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return { ref, width: width > 0 ? width : FALLBACK_WIDTH };
}

export function TideChart({
  stationName,
  curve,
  extremes,
}: {
  stationName: string;
  curve: TidePredictionPoint[];
  extremes: TideExtreme[];
}): JSX.Element | null {
  const { ref, width } = useContainerWidth();
  const [active, setActive] = useState<number | null>(null);

  const model = useMemo(() => {
    const time = timeDomain(curve, extremes);
    const values = valueDomain([...curve.map((p) => p.value), ...extremes.map((e) => e.value)]);
    if (!time || !values) return null;
    const plotRight = width - MARGIN.right;
    const plotBottom = HEIGHT - MARGIN.bottom;
    const xs = linearScale(time.start.getTime(), time.end.getTime(), MARGIN.left, plotRight);
    const ys = linearScale(values.min, values.max, plotBottom, MARGIN.top);
    const points = curve.map((p) => ({ x: xs(p.date.getTime()), y: ys(p.value) }));
    const linePath = catmullRomPath(points);
    const areaPath =
      points.length > 1 ? `${linePath} L${points[points.length - 1].x.toFixed(2)},${plotBottom} L${points[0].x.toFixed(2)},${plotBottom} Z` : '';
    return {
      time,
      values,
      xs,
      ys,
      plotRight,
      plotBottom,
      points,
      linePath,
      areaPath,
      times: curve.map((p) => p.date.getTime()),
      days: dayTicks(time.start, time.end),
      description: describeTideChart(stationName, curve, extremes, time, values),
    };
  }, [curve, extremes, width, stationName]);

  if (!model) return null;
  const { xs, ys, plotRight, plotBottom, times } = model;

  const activePoint = active != null && active >= 0 && active < curve.length ? curve[active] : null;
  const activeExtreme = activePoint ? extremeNear(extremes, activePoint.date.getTime(), EXTREME_TOLERANCE_MS) : null;

  const onPointerMove = (e: PointerEvent<SVGSVGElement>) => {
    if (times.length === 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const px = ((e.clientX - rect.left) / rect.width) * width;
    setActive(nearestIndex(times, xs.invert(px)));
  };

  const onKeyDown = (e: KeyboardEvent<SVGSVGElement>) => {
    if (times.length === 0) return;
    const current = active ?? Math.max(0, nearestIndex(times, Date.now()));
    let next: number | null = current;
    switch (e.key) {
      case 'ArrowRight':
        next = Math.min(times.length - 1, current + 1);
        break;
      case 'ArrowLeft':
        next = Math.max(0, current - 1);
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = times.length - 1;
        break;
      case 'Escape':
        next = null;
        break;
      default:
        return;
    }
    e.preventDefault();
    setActive(next);
  };

  const labelAnchor = (x: number): 'start' | 'middle' | 'end' => {
    if (x < MARGIN.left + 18) return 'start';
    if (x > plotRight - 18) return 'end';
    return 'middle';
  };
  const clampLabelX = (x: number) => Math.min(Math.max(x, MARGIN.left + 12), plotRight - 12);

  const activeX = activePoint ? xs(activePoint.date.getTime()) : 0;
  const tooltipOnLeft = activeX > width * 0.6;

  return (
    <div className="sd-chart" ref={ref}>
      <svg
        width={width}
        height={HEIGHT}
        viewBox={`0 0 ${width} ${HEIGHT}`}
        role="img"
        aria-label={model.description}
        tabIndex={0}
        onPointerMove={onPointerMove}
        onPointerLeave={() => setActive(null)}
        onKeyDown={onKeyDown}
        onBlur={() => setActive(null)}
      >
        {/* Y gridlines and ticks */}
        {model.values.ticks.map((tick) => {
          const y = ys(tick);
          return (
            <g key={`y-${tick}`}>
              <line className="sd-grid" x1={MARGIN.left} x2={plotRight} y1={y} y2={y} />
              <text className="sd-axis-text" x={MARGIN.left - 6} y={y + 3.5} textAnchor="end">
                {formatFeet(tick)}
              </text>
            </g>
          );
        })}
        {/* Day gridlines and labels */}
        {model.days.map((day) => {
          const x = xs(day.getTime());
          return (
            <g key={`d-${day.getTime()}`}>
              <line className="sd-grid" x1={x} x2={x} y1={MARGIN.top} y2={plotBottom} />
              <text className="sd-axis-text" x={x} y={HEIGHT - 7} textAnchor={labelAnchor(x)}>
                {formatMonthDay(day)}
              </text>
            </g>
          );
        })}
        <line className="sd-axis" x1={MARGIN.left} x2={plotRight} y1={plotBottom} y2={plotBottom} />

        {/* Curve */}
        {model.areaPath ? <path className="sd-area" d={model.areaPath} /> : null}
        {model.linePath ? <path className="sd-curve" d={model.linePath} /> : null}

        {/* High/low markers with their heights */}
        {extremes.map((e) => {
          const x = xs(e.date.getTime());
          const y = ys(e.value);
          return (
            <g key={`${e.t}-${e.isHigh ? 'H' : 'L'}`}>
              <circle className={`sd-extreme ${e.isHigh ? 'sd-extreme-high' : 'sd-extreme-low'}`} cx={x} cy={y} r={4} />
              <text className="sd-extreme-label" x={clampLabelX(x)} y={e.isHigh ? y - 9 : y + 15}>
                {formatFeet(e.value)}
              </text>
            </g>
          );
        })}

        {/* Crosshair */}
        {activePoint ? (
          <g>
            <line className="sd-crosshair" x1={activeX} x2={activeX} y1={MARGIN.top} y2={plotBottom} />
            <circle className="sd-active-dot" cx={activeX} cy={ys(activePoint.value)} r={4} />
          </g>
        ) : null}
      </svg>

      {activePoint ? (
        <div
          className={`sd-tooltip ${tooltipOnLeft ? 'is-left' : ''}`}
          style={{ left: activeX }}
          role="status"
          aria-live="polite"
        >
          <div className="sd-tooltip-time">{formatDateTime(activePoint.date)}</div>
          <div className="sd-tooltip-row">
            <span className={`sd-tooltip-key ${activeExtreme && !activeExtreme.isHigh ? 'is-low' : ''}`} aria-hidden="true" />
            <span className="sd-tooltip-value">{formatFeet(activePoint.value)} ft</span>
            <span className="sd-tooltip-label">{activeExtreme ? (activeExtreme.isHigh ? 'High tide' : 'Low tide') : 'MLLW'}</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}
